import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Monitor, Edit, Save, X, Smartphone, AlertTriangle, Loader2 } from "lucide-react";
import { useQuery as useConvexQuery, useMutation as useConvexMutation, usePaginatedQuery } from "convex/react";
import { api } from "../../api";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { type GenericId as Id } from "convex/values";

interface LevelScopeTabProps {
  levelId: string;
}

interface ConvexDevice {
  _id: string;
  deviceConfig: Id<"deviceConfigs">;
  jssid?: number;
  model?: string;
  serial?: string;
}

export const LevelScopeTab = ({ levelId }: LevelScopeTabProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<string>>(new Set());
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  // Get current user role - handle unauthenticated case
  const { user, isAuthenticated } = useCurrentUser();
  const userRole = user?.roles?.[0]?._id;

  const allDevicesQuery = usePaginatedQuery(
    api.devices.list.all, 
    isAuthenticated ? {} : "skip",
    { initialNumItems: 1000 }
  );

  // Skip the problematic inLevel query and filter client-side instead
  console.log('LevelScopeTab: Using client-side filtering instead of inLevel query for level:', levelId);
  
  // No levelDevicesQuery - we'll filter from allDevicesQuery instead
  const levelDevicesQuery = { results: [], isLoading: false, status: "LoadingFirstPage" };

  // Update device level mutation
  const updateDeviceLevelMutation = useConvexMutation(api.devices.level.update);

  const allDevices = (allDevicesQuery?.results || []).map(device => ({
    ...device,
    _id: device.id
  })) as ConvexDevice[];
  
  // Filter devices assigned to this level from allDevices instead of using problematic inLevel query
  const assignedDevices = allDevices.filter(device => {
    // Check if device has level information and matches current level
    const deviceData = device as any;
    const deviceLevel = deviceData.level;
    
    console.log('LevelScopeTab: Checking device:', device._id, 'level:', deviceLevel, 'target level:', levelId);
    
    // Match by level name since levelId is the level name
    return deviceLevel === levelId || (typeof deviceLevel === 'object' && deviceLevel?.name === levelId);
  });
  
  console.log('LevelScopeTab: Found assigned devices:', assignedDevices.length, 'for level:', levelId);
  const isLoadingDevices = allDevicesQuery.isLoading;

  const assignedDeviceIds = assignedDevices.map(device => device._id);
  const assignedDeviceConfigIds = new Set(assignedDevices.map(device => device.deviceConfig));

  const handleEditMode = () => {
    setIsEditMode(true);
    setSelectedDeviceIds(new Set(assignedDeviceIds));
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setSelectedDeviceIds(new Set());
  };

  const handleDeviceToggle = (deviceId: string, checked: boolean) => {
    const newSelected = new Set(selectedDeviceIds);
    if (checked) {
      newSelected.add(deviceId);
    } else {
      newSelected.delete(deviceId);
    }
    setSelectedDeviceIds(newSelected);
  };

  const handleSaveChanges = async () => {
    const selectedArray = Array.from(selectedDeviceIds);
    const currentlyAssigned = new Set(assignedDeviceIds);
    
    const devicesToMove = selectedArray.filter(deviceId => {
      const device = allDevices.find(d => d._id === deviceId);
      return device && !assignedDeviceConfigIds.has(device.deviceConfig);
    });

    if (devicesToMove.length > 0) {
      setShowWarningDialog(true);
    } else {
      await performSave();
    }
  };

  // Fetch levels for finding the level object by name
  const allLevelsQuery = useConvexQuery(api.levels.list.all, {
    paginationOpts: {
      cursor: null,
      numItems: 100
    }
  });

  const performSave = async () => {
    setIsSaving(true);
    try {
      const selectedArray = Array.from(selectedDeviceIds);
      const currentlyAssigned = new Set(assignedDeviceIds);
      
      const devicesToAdd = selectedArray.filter(deviceId => !currentlyAssigned.has(deviceId));
      
      // Find the level object by name to get its _id
      const levelObject = allLevelsQuery?.page?.find(level => level.name === levelId);
      
      if (!levelObject && devicesToAdd.length > 0) {
        throw new Error("Level not found");
      }
      
      for (const deviceId of devicesToAdd) {
        await updateDeviceLevelMutation({
          device: deviceId as Id<"devices">,
          level: levelObject!._id as Id<"levels">
        });
      }
      
      setIsEditMode(false);
      setSelectedDeviceIds(new Set());
      setShowWarningDialog(false);
      
      toast({
        title: "Success",
        description: `Successfully updated device assignments. ${devicesToAdd.length} device(s) added to level.`,
      });
      
    } catch (error) {
      console.error('Error saving device assignments:', error);
      toast({
        title: "Error",
        description: "Failed to save device assignments. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingDevices) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-medium">Scope</h3>
            <p className="text-gray-600">Define which devices this level applies to</p>
          </div>
          <Button disabled>
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
        </div>
        <div className="animate-pulse flex-1">
          <Card className="h-full">
            <CardContent className="p-6">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-4 bg-gray-200 rounded"></div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const devicesToDisplay = isEditMode ? allDevices : assignedDevices;

  return (
    <>
      <div className="h-full flex flex-col p-6">
        {/* Header with Edit/Save buttons */}
        <div className="flex justify-between items-center mb-6 flex-shrink-0">
          <div>
            <h3 className="text-lg font-medium">Scope</h3>
            <p className="text-gray-600">Define which devices this level applies to</p>
          </div>
          <div className="flex items-center gap-2">
            {isEditMode ? (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={handleEditMode}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </div>

        {/* Summary info when not in edit mode */}
        {!isEditMode && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2 text-blue-800">
              <Monitor className="w-4 h-4" />
              <span className="font-medium">
                {assignedDevices.length} device{assignedDevices.length !== 1 ? 's' : ''} assigned to this level
              </span>
            </div>
            {assignedDevices.length > 0 && (
              <p className="text-sm text-blue-700 mt-1">
                Click "Edit" to modify device assignments
              </p>
            )}
          </div>
        )}

        {/* Edit mode summary */}
        {isEditMode && (
          <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center space-x-2 text-orange-800">
              <Edit className="w-4 h-4" />
              <span className="font-medium">
                {selectedDeviceIds.size} device{selectedDeviceIds.size !== 1 ? 's' : ''} selected
              </span>
            </div>
            <p className="text-sm text-orange-700 mt-1">
              Select devices to assign to this level
            </p>
          </div>
        )}

        {/* Devices Table */}
        <Card className="flex-1 flex flex-col min-h-0">
          <CardContent className="flex-1 flex flex-col min-h-0 p-0">
            {devicesToDisplay.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center py-8">
                  <Smartphone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    {isEditMode ? 'No devices available' : 'No devices assigned'}
                  </h4>
                  <p className="text-gray-500">
                    {isEditMode 
                      ? 'There are no devices in the system to assign.' 
                      : 'No devices are currently assigned to this level.'
                    }
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex-1 border rounded-lg overflow-hidden flex flex-col">
                <div className="overflow-auto flex-1">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10 shadow-sm">
                      <TableRow>
                        {isEditMode && <TableHead className="w-12 bg-white border-b"></TableHead>}
                        <TableHead className="bg-white border-b">Device</TableHead>
                        <TableHead className="bg-white border-b">Serial Number</TableHead>
                        <TableHead className="bg-white border-b">Model</TableHead>
                        <TableHead className="bg-white border-b">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {devicesToDisplay.map((device) => {
                        const isSelected = selectedDeviceIds.has(device._id);
                        const isCurrentlyAssigned = assignedDeviceIds.includes(device._id);
                        
                        return (
                          <TableRow key={device._id}>
                            {isEditMode && (
                              <TableCell>
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(checked) => handleDeviceToggle(device._id, checked as boolean)}
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                  <Monitor className="w-4 h-4 text-blue-700" />
                                </div>
                                <div>
                                  <div className="font-medium">
                                    Device {device.jssid || device._id.slice(-8)}
                                  </div>
                                  {isEditMode && isCurrentlyAssigned && (
                                    <Badge variant="secondary" className="text-xs mt-1">
                                      Currently assigned
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-gray-600">
                              {device.serial || 'N/A'}
                            </TableCell>
                            <TableCell className="text-gray-600">
                              {device.model || 'Unknown'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="default" className="bg-green-100 text-green-800">
                                Active
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Level Reassignment Warning
            </AlertDialogTitle>
            <AlertDialogDescription>
              Some of the selected devices may be assigned to other levels. 
              Assigning them to this level will move them from their current level.
              This will change the restrictions and policies applied to these devices.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={performSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Yes, Assign Devices'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
